import PageHeader from "@/components/page-header";
import { AccountForm } from "@/components/registrant/account-form";
import { ContextHelpButton } from "@/components/registrant/help/context-help-button";
import LoadingScreen from "@/components/ui/loading-screen";
import { toast } from "@/components/ui/sonner";
import {
  getRegistrantAccountOptions,
  updateRegistrantAccountMutation,
} from "@/hera-api/client/@tanstack/react-query.gen";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { getQueryErrorMessage } from "@/lib/utils";
import type { AccountSchema } from "@/types/account";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { z } from "zod";

export const Route = createFileRoute("/account-setup/complete-profile")({
  component: CompleteProfile,
});

function CompleteProfile() {
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const navigate = useNavigate();

  const {
    data,
    isLoading: isQueryLoading,
    error: queryError,
    refetch,
  } = useQuery({
    ...getRegistrantAccountOptions(),
    enabled: isAuthSetupComplete,
  });

  const saveUserMutation = useMutation({
    ...updateRegistrantAccountMutation(),
  });

  const handleSubmit = async (formData: z.infer<typeof AccountSchema>) => {
    try {
      await saveUserMutation.mutateAsync({
        body: formData,
      });
      await refetch();
      toast({
        title: "Success",
        description: "Account information updated successfully",
        variant: "secondary",
      });
      navigate({ to: "/account-setup/verify-email" });
    } catch (error) {
      toast({
        title: "Error",
        description: `${getQueryErrorMessage(error)}`,
        variant: "destructive",
      });
    }
  };

  if (!isAuthSetupComplete || isQueryLoading || queryError) {
    return <LoadingScreen />;
  }

  return (
    <div className="flex flex-col container mx-auto max-w-3xl">
      <PageHeader
        title="Complete Your Account Setup"
        subtitle="Please provide your details to continue"
        className="mb-6"
        action={<ContextHelpButton articleId="account-setup" />}
      />
      <AccountForm
        initialData={data}
        onSubmit={handleSubmit}
        submitButtonText="Complete Setup"
      />
    </div>
  );
}
